import React from 'react';
import { View, StyleSheet, ViewStyle, Platform } from 'react-native';
import { colors, radius, shadows } from '../../styles';
import { useTheme } from '../../context/ThemeContext';
import { surfaceFill } from '../../styles/platformBackgrounds';

interface CardProps {
    children: React.ReactNode;
    variant?: 'default' | 'glass' | 'outline';
    style?: ViewStyle;
    contentStyle?: ViewStyle;
    onPress?: () => void;
}

export const Card: React.FC<CardProps> = ({
    children,
    variant = 'default',
    style,
    contentStyle,
    onPress,
}) => {
    const { colors: themeColors, isDark } = useTheme();

    const getCardStyle = (): any => {
        // CORE-639 §3 — default cards use the semi-transparent surface token so
        // the global background shows through, with the soft --shadow-surface
        // and a --border-subtle hairline for definition on the busy backdrop.
        // CORE-1733 (возврат QA) — обычная карточка — поверхность v4 (градиент и контур
        // статьи 110756133), а не плоская полупрозрачная заливка.
        const baseStyle: any[] = [styles.card, {
            ...(Platform.OS === 'web'
                ? surfaceFill(isDark ? 'dark' : 'light')
                : { backgroundColor: themeColors.surfacePrimary, borderWidth: 1, borderColor: themeColors.border }),
            ...(Platform.OS === 'web' ? { boxShadow: themeColors.shadowSurface } : {}),
        }];
        if (variant === 'glass') baseStyle.push(styles.glass, isDark && styles.glassDark, { borderColor: themeColors.border });
        if (variant === 'outline') baseStyle.push(styles.outline, { borderColor: themeColors.border });
        return [baseStyle, style];
    };

    if (onPress) {
        const { TouchableOpacity } = require('react-native');
        return (
            <TouchableOpacity onPress={onPress} activeOpacity={0.8} style={getCardStyle()}>
                <View style={[styles.content, contentStyle]}>
                    {children}
                </View>
            </TouchableOpacity>
        );
    }

    return (
        <View style={getCardStyle()}>
            <View style={[styles.content, contentStyle]}>
                {children}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    card: {
        backgroundColor: colors.white,
        borderRadius: 40,
        overflow: 'hidden',
        ...Platform.select({
            web: {
                boxShadow: '0 40px 80px -20px rgba(107, 94, 85, 0.08)',
                transition: 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.5s ease',
            },
        }),
    },
    content: {
        padding: 32,
    },
    glass: {
        backgroundColor: 'rgba(255, 255, 255, 0.45)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.7)',
        ...Platform.select({
            web: {
                backdropFilter: 'blur(100px) saturate(180%)',
                WebkitBackdropFilter: 'blur(100px) saturate(180%)',
            },
        }),
    },
    glassDark: {
        backgroundColor: 'rgba(30, 30, 30, 0.45)',
        borderColor: 'rgba(255, 255, 255, 0.08)',
    },
    outline: {
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderColor: colors.sand,
        ...Platform.select({
            web: {
                boxShadow: 'none',
            },
        }),
    },
});
